/**
 * These helpers provide a consistent contract for the
 * Dashboard Instrumentation Framework (see engineering guide):
 * @see {@link https://docs.google.com/document/d/1UKedwoC3F0g9vV4FejsmsYg1e8JWMtojTNaxUIoO7TY/edit?usp=sharing }
 */
import { isObject, isString } from './type-guards';
export const EVENT_NAME_VIEW_FEATURE = 'View Feature';
export const EVENT_NAME_CLICK_FEATURE = 'Click Feature';
export const EVENT_NAME_CLICK_LINK = 'Click Link';
export const EVENT_NAME_ERROR = 'Error';
/**
 * Gets the name of a valid instrumentation framework event
 */
export function getFeatureName(eventName, eventProps) {
    var _a;
    const validEvent = isString(eventName) &&
        [
            EVENT_NAME_VIEW_FEATURE,
            EVENT_NAME_CLICK_FEATURE,
            EVENT_NAME_CLICK_LINK,
            EVENT_NAME_ERROR,
        ].includes(eventName);
    const validProps = isObject(eventProps) &&
        (('feature_id' in eventProps && 'feature_name' in eventProps) ||
            ('featureId' in eventProps && 'featureName' in eventProps));
    if (validEvent && validProps) {
        return (_a = eventProps.feature_name) !== null && _a !== void 0 ? _a : eventProps.featureName;
    }
    return '';
}
/**
 * Tracks the standard "View Feature" event
 * @see {@link https://docs.google.com/document/d/1dFs91wtBm_afiHsopKyVTUZH4RoYtTtjdh5dKmPfKjI/edit?pli=1#bookmark=id.x7dyq52kvjen }
 *
 * @param {CDP} instance An instance of the main CDP class
 * @param {ViewFeatureArgs} properties Dictionary of valid event properties
 * @param {string} properties.featureName The name of the feature
 * @param {string} properties.featureId Unique identifier for the feature; see
 *                 {@link https://docs.google.com/spreadsheets/d/1n5PvCFwxjMvjOdHviRiVnqcG06cW0FOLVnjZy4ckWwg/edit?usp=sharing | this spreadsheet}
 *                 to view/register IDs for new or existing features
 * @param {?string} properties.featureParentId Unique identifier (if applicable) for the parent feature. NULL if none.
 * @param {string} properties.featureFormat Format of the feature (module, modal, blade, etc.)
 * @param {string} properties.eventDescription Description of what the event is achieving
 * @param {boolean} properties.isDefaultView Boolean indicating whether the view in question is the default view, or whether the user has adjusted.
 *                                           We consider the default view the one that is present initially after user login.
 * @param {?Object} properties.featureLayout JSON dictionary with current snapshot of the feature
 * @param {?Object} properties.additionalParameters JSON dictionary with feature / view specific properties
 * @param {boolean} properties.isTaskCompleted Flag noting whether the action was a success
 */
export function trackViewFeature(instance, { featureName, featureId, featureParentId = null, featureFormat, eventDescription, isDefaultView, featureLayout = null, additionalParameters = null, isTaskCompleted, }) {
    /**
     * All properties that get passed to the data API should use snake case
     * to adhere to CDP's guidelines on data quality.
     *
     * @see {@link https://docs.google.com/document/d/1N8erIAE3CydeKuSLX0zMaLWskDy6PONSeAxErvvOEcU/edit#heading=h.jsuxzjdj57cr}
     */
    const properties = {
        feature_name: featureName,
        feature_id: featureId,
        feature_parent_id: featureParentId,
        feature_format: featureFormat,
        event_description: eventDescription,
        is_default_view: isDefaultView,
        feature_layout: featureLayout,
        additional_parameters: additionalParameters,
        is_task_completed: isTaskCompleted,
    };
    instance.trackV2({
        eventName: EVENT_NAME_VIEW_FEATURE,
        eventProps: properties,
    });
}
/**
 * Tracks the standard "Click Feature" event
 * @see {@link https://docs.google.com/document/d/1dFs91wtBm_afiHsopKyVTUZH4RoYtTtjdh5dKmPfKjI/edit?pli=1#bookmark=id.k9t6hhrbr7d9 }
 *
 * @param {CDP} instance An instance of the main CDP class
 * @param {ClickFeatureArgs} properties Dictionary of valid event properties
 * @param {string} properties.featureName The name of the feature
 * @param {string} properties.featureId Unique identifier for the feature; see
 *                 {@link https://docs.google.com/spreadsheets/d/1n5PvCFwxjMvjOdHviRiVnqcG06cW0FOLVnjZy4ckWwg/edit?usp=sharing | this spreadsheet}
 *                 to view/register IDs for new or existing features
 * @param {?string} properties.featureParentId Unique identifier (if applicable) for the parent feature. NULL if none.
 * @param {string} properties.featureFormat Format of the feature (module, modal, blade, etc.)
 * @param {string} properties.eventDescription Description of what the event is achieving
 * @param {string} properties.actionItem General description of the action being taken. Values should take the form of:
 *                                       <action being taken>_<object / section where action is taken>
 * @param {?string} properties.subActionItem Additional description to describe the action if needed, null otherwise
 * @param {?Object} properties.additionalParameters JSON dictionary with feature / click specific properties
 * @param {boolean} properties.isTaskCompleted Flag noting whether the action was a success
 */
export function trackClickFeature(instance, { featureName, featureId, featureParentId = null, featureFormat, eventDescription, actionItem, subActionItem = null, additionalParameters = null, isTaskCompleted, }) {
    /**
     * All properties that get passed to the data API should use snake case
     * to adhere to CDP's guidelines on data quality.
     *
     * @see {@link https://docs.google.com/document/d/1N8erIAE3CydeKuSLX0zMaLWskDy6PONSeAxErvvOEcU/edit#heading=h.jsuxzjdj57cr}
     */
    const properties = {
        feature_name: featureName,
        feature_id: featureId,
        feature_parent_id: featureParentId,
        feature_format: featureFormat,
        event_description: eventDescription,
        action_item: actionItem,
        sub_action_item: subActionItem,
        additional_parameters: additionalParameters,
        is_task_completed: isTaskCompleted,
    };
    instance.trackV2({
        eventName: EVENT_NAME_CLICK_FEATURE,
        eventProps: properties,
    });
}
/**
 * Tracks the standard "Click Link" event
 * @see {@link https://docs.google.com/document/d/1dFs91wtBm_afiHsopKyVTUZH4RoYtTtjdh5dKmPfKjI/edit?pli=1#bookmark=id.oor03bh6ymou }
 *
 * @param {CDP} instance An instance of the main CDP class
 * @param {ClickLinkArgs} properties Dictionary of valid event properties
 * @param {string} properties.featureName The name of the feature (if applicable)
 * @param {string} properties.featureId Unique identifier for the feature (if applicable); see
 *                 {@link https://docs.google.com/spreadsheets/d/1n5PvCFwxjMvjOdHviRiVnqcG06cW0FOLVnjZy4ckWwg/edit?usp=sharing | this spreadsheet}
 *                 to view/register IDs for new or existing features
 * @param {?string} properties.featureParentId Unique identifier (if applicable) for the parent feature. NULL if none.
 * @param {string} properties.featureFormat Format of the feature (module, modal, blade, etc.)
 * @param {string} properties.linkText Link text as displayed to the end user
 * @param {string} properties.linkTitle Common link title, agnostic of user (identical across locales, etc)
 * @param {?string} properties.linkUrl Full URL, could be external to site.
 */
export function trackClickLink(instance, { featureName, featureId, featureParentId = null, featureFormat, linkText, linkTitle, linkUrl, }) {
    /**
     * All properties that get passed to the data API should use snake case
     * to adhere to CDP's guidelines on data quality.
     *
     * @see {@link https://docs.google.com/document/d/1N8erIAE3CydeKuSLX0zMaLWskDy6PONSeAxErvvOEcU/edit#heading=h.jsuxzjdj57cr}
     */
    const properties = {
        feature_name: featureName,
        feature_id: featureId,
        feature_parent_id: featureParentId,
        feature_format: featureFormat,
        link_text: linkText,
        link_title: linkTitle,
        link_url: linkUrl,
    };
    instance.trackV2({
        eventName: EVENT_NAME_CLICK_LINK,
        eventProps: properties,
    });
}
/**
 * Tracks the standard "Error" event
 * @see {@link https://docs.google.com/document/d/1dFs91wtBm_afiHsopKyVTUZH4RoYtTtjdh5dKmPfKjI/edit?pli=1#heading=h.3u3yh3a7d6jm }
 *
 * @param {CDP} instance An instance of the main CDP class
 * @param {ErrorArgs} properties Dictionary of valid event properties
 * @param {string} properties.featureName The name of the feature (if applicable)
 * @param {string} properties.featureId Unique identifier for the feature (if applicable); see
 *                 {@link https://docs.google.com/spreadsheets/d/1n5PvCFwxjMvjOdHviRiVnqcG06cW0FOLVnjZy4ckWwg/edit?usp=sharing | this spreadsheet}
 *                 to view/register IDs for new or existing features
 * @param {?string} properties.featureParentId Unique identifier (if applicable) for the parent feature. NULL if none.
 * @param {string} properties.featureFormat Format of the feature (module, modal, blade, etc.)
 * @param {string} properties.eventDescription Verbose description of the event
 * @param {string} properties.eventCalled String with the type of event action meant to be taken
 * @param {string} properties.errorType Type of error being thrown (404, etc.)
 * @param {?string} properties.errorMessage Error message (if applicable)
 * @param {?Object} properties.additionalParameters JSON dictionary with feature / click specific properties
 * @param {boolean} properties.isTaskCompleted Flag noting whether the action is the completion of the task
 */
export function trackError(instance, { 
// Feature level
featureName, featureId, featureParentId = null, featureFormat, 
// Error level
eventDescription, eventCalled, errorType, errorMessage = null, additionalParameters = null, isTaskCompleted, }) {
    /**
     * All properties that get passed to the data API should use snake case
     * to adhere to CDP's guidelines on data quality.
     *
     * @see {@link https://docs.google.com/document/d/1N8erIAE3CydeKuSLX0zMaLWskDy6PONSeAxErvvOEcU/edit#heading=h.jsuxzjdj57cr}
     */
    const properties = {
        // Feature level
        feature_name: featureName,
        feature_id: featureId,
        feature_parent_id: featureParentId,
        feature_format: featureFormat,
        // Error level
        event_description: eventDescription,
        event_called: eventCalled,
        error_type: errorType,
        error_message: errorMessage,
        additional_parameters: additionalParameters,
        is_task_completed: isTaskCompleted,
    };
    instance.trackV2({
        eventName: EVENT_NAME_ERROR,
        eventProps: properties,
    });
}
//# sourceMappingURL=data:application/json;base64,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